<script lang="ts" setup>
const emit = defineEmits(['advancedSearch', 'clearSearch'])
const props = defineProps({
  fields: Object,
  values: Object,
  genreLoading: Boolean,
  genresList: Array<Object>,
  instrumentsList: Array<Object>,
  instrumentsLoading: Boolean,
})

function addAccordionItem(item, model, keyValue = 'slug', isList = true) {
  if (isList) {
    props.values[model] = [...props.values[model], item[keyValue]]
  } else {
    props.values[model] = item[keyValue]
  }
}

const showListGenreDialog = ref(false)
const showWithInstrumentDialog = ref(false)
const showWithoutInstrumentDialog = ref(false)
const getGenresList = async () => {
  showListGenreDialog.value = true
}
const getInstrumentsList = async (type) => {
  showListGenreDialog.value = true
  if (type === 'with') {
    showWithInstrumentDialog.value = true
  } else {
    showWithoutInstrumentDialog.value = true
  }
}
</script>
<template>
  <div class="px-8 py-12 md:px-12 lg:px-24">
    <div v-for="field in fields" :key="field.id">
      <div v-if="field.type === 'text'" class="my-2">
        <FormTextField
          v-model="values[field.model]"
          :label="field.label"
          :placeholder="field.placeholder"
          @keyup.enter="emit('advancedSearch')"
        />
      </div>

      <div v-if="field.type === 'checkbox'" class="my-2">
        <FormCheckbox
          v-model="values[field.model]"
          :name="field.model"
          :label="field.label"
        />
      </div>
      <div v-if="field.type === 'search'" class="my-2">
        <p class="font-bold first-letter:uppercase">{{ field.label }}</p>
        <SearchBox
          :values="values"
          :search-collection="field.table"
          :search-fields="field.fields"
          :search-include="field.include"
          :model="field.model"
          :autocomplete="true"
          :display-function="field.displayFn"
          :key-value="field.keyValue"
          :default-input="values[field.model]"
          :filter-by="field.filterBy"
        />
      </div>
      <div v-if="field.model === 'genres'">
        <BasicButton
          :disabled="genreLoading"
          :loading="genreLoading"
          plain
          @click="getGenresList"
        >
          <span class="first-letter:uppercase">
            {{ $t('advanced_search.works.list_genres', 2) }}
          </span>
        </BasicButton>
        <FeedbackDialog v-model="showListGenreDialog">
          <template #title>{{
            $t('advanced_search.works.list_genres', 1)
          }}</template>
          <div>
            <ListAccordion
              v-for="genre in genresList"
              :key="genre.id"
              :item="genre"
              :indent="0"
              has-select
              @add="
                (item) => addAccordionItem(item, field.model, 'nameFr', false)
              "
            />
          </div>
        </FeedbackDialog>
      </div>
      <div v-if="field.type === 'select'" class="my-2">
        <FormSelect
          ref="formSelect"
          :options="field.options"
          :label="field.label"
          :data="values"
          :model="field.model"
        />
      </div>
      <div v-if="field.type === 'autocomplete'" class="my-2">
        <FormAutocomplete
          :data.sync="values"
          :options="field.options"
          :label="field.label"
          :model="field.model"
          :multiple="field.multiple"
          :display-fn="field.displayFn"
          :get-option="field.getOption"
        />
        <div v-if="field.model === 'withInstruments'">
          <BasicButton
            :disabled="instrumentsLoading"
            :loading="instrumentsLoading"
            plain
            @click="getInstrumentsList('with')"
            class="mt-2"
          >
            <span class="first-letter:uppercase">
              {{ $t('advanced_search.works.list_instruments', 2) }}
            </span>
          </BasicButton>
          <FeedbackDialog v-model="showWithInstrumentDialog">
            <template #title>{{
              $t('advanced_search.works.list_instruments', 1)
            }}</template>
            <div>
              <ListAccordion
                v-for="instrument in instrumentsList"
                :key="instrument.id"
                :item="instrument"
                has-select
                @add="(item) => addAccordionItem(item, field.model)"
                :indent="0"
              />
            </div>
          </FeedbackDialog>
        </div>
        <div v-if="field.model === 'withoutInstruments'">
          <BasicButton
            :disabled="instrumentsLoading"
            :loading="instrumentsLoading"
            plain
            @click="getInstrumentsList('without')"
            class="mt-2"
          >
            <span class="first-letter:uppercase">
              {{ $t('advanced_search.works.list_instruments', 2) }}
            </span>
          </BasicButton>
          <FeedbackDialog v-model="showWithoutInstrumentDialog">
            <template #title>{{
              $t('advanced_search.works.list_instruments', 1)
            }}</template>
            <div>
              <ListAccordion
                v-for="instrument in instrumentsList"
                :key="instrument.id"
                :item="instrument"
                has-select
                @add="(item) => addAccordionItem(item, field.model)"
                :indent="0"
              />
            </div>
          </FeedbackDialog>
        </div>
      </div>

      <div v-if="field.type === 'range'" class="my-2">
        <FormMinMaxField
          :id="field.id"
          :model="field.model"
          :data="values"
          :name="field.name"
          :label="field.label"
          :min="field.min"
          :max="field.max"
        />
      </div>
    </div>
    <div class="flex justify-between">
      <BasicButton @click="emit('advancedSearch')">
        <span class="first-letter:uppercase">
          {{ $t('labels.submit') }}
        </span>
      </BasicButton>
      <BasicButton @click="emit('clearSearch')" plain>
        <span class="first-letter:uppercase">
          {{ $t('labels.clear') }}
        </span>
      </BasicButton>
    </div>
  </div>
</template>
